/**
 * One person's access, in one place: which doors they have, and — if one of them is
 * the portal — what they may do there.
 *
 * Used on the employee's own page and in Settings → Users, so the answer to "what can
 * Marco see?" is wherever someone goes looking for Marco.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Switch } from '@/components/Switch';
import { ErrorNotice, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api, ApiError, SECTIONS, type PermissionGrid as Grid, type Section, type User } from '@/lib/api';

import { PermissionGrid, summarise } from './PermissionGrid';
import { usePermissions } from './usePermissions';

export function AccessPanel({ user, isSelf }: { user: User; isSelf: boolean }) {
  const { t } = useI18n();
  const { can } = usePermissions();
  const canEdit = can('users', 'write');

  return (
    <div className="space-y-5">
      <Doors user={user} isSelf={isSelf} disabled={!canEdit} />

      {user.role === 'admin' ? (
        <Note>{t('access.fullAccess')}</Note>
      ) : !user.portalAccess ? (
        <Note>{t('access.appOnly')}</Note>
      ) : (
        <PortalGrid userId={user.id} isSelf={isSelf} disabled={!canEdit} />
      )}
    </div>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return <p className="rounded-lg bg-surface-alt px-3 py-2.5 text-sm text-muted">{children}</p>;
}

/**
 * The two doors as two switches. Closing the portal door moves someone to the field
 * role and resets their grid — the server does that and says why in users.ts; the
 * hint here says it to the person pressing the switch.
 */
function Doors({ user, isSelf, disabled }: { user: User; isSelf: boolean; disabled: boolean }) {
  if (!user.email) return <CreateAccount user={user} disabled={disabled} />;
  return <DoorSwitches user={user} isSelf={isSelf} disabled={disabled} />;
}

/**
 * Someone added without an email: they have no account, and the address is asked for
 * here — at the moment it is needed — together with where they will sign in.
 */
function CreateAccount({ user, disabled }: { user: User; disabled: boolean }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [portal, setPortal] = useState(false);
  const [app, setApp] = useState(true);

  const create = useMutation({
    mutationFn: () =>
      api.setUserAccess(user.id, { portalAccess: portal, appAccess: app, email: email.trim().toLowerCase() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      await queryClient.invalidateQueries({ queryKey: ['user', user.id] });
      await queryClient.invalidateQueries({ queryKey: ['permissions', user.id] });
    },
  });

  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && (portal || app);

  return (
    <div className="space-y-3">
      <p className="rounded-lg bg-surface-alt px-3 py-2.5 text-sm text-muted">{t('access.noAccountYet')}</p>
      {disabled ? null : (
        <>
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-muted">{t('access.email')}</span>
            <input
              className="input"
              type="email"
              value={email}
              placeholder="nome@esempio.it"
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <div className="divide-y divide-border rounded-lg border border-border">
            {[
              { key: 'portal', on: portal, set: setPortal, title: t('access.doorPortal'), hint: t('access.doorPortalHint') },
              { key: 'app', on: app, set: setApp, title: t('access.doorApp'), hint: t('access.doorAppHint') },
            ].map((door) => (
              <div key={door.key} className="flex items-center justify-between gap-4 px-3 py-2.5">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{door.title}</div>
                  <div className="mt-0.5 text-xs leading-snug text-muted">{door.hint}</div>
                </div>
                <Switch checked={door.on} label={door.title} onChange={door.set} />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="btn-primary"
              disabled={!valid || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? t('common.saving') : t('access.createAccount')}
            </button>
            <span className="text-xs text-muted">{t('access.createAccountHint')}</span>
          </div>
          {create.error ? (
            <p className="text-sm text-danger">
              {create.error instanceof ApiError ? create.error.message : t('common.saveError')}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

function DoorSwitches({ user, isSelf, disabled }: { user: User; isSelf: boolean; disabled: boolean }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const change = useMutation({
    mutationFn: (next: { portalAccess: boolean; appAccess: boolean }) => api.setUserAccess(user.id, next),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      await queryClient.invalidateQueries({ queryKey: ['user', user.id] });
      await queryClient.invalidateQueries({ queryKey: ['permissions', user.id] });
    },
  });

  const doors = [
    {
      key: 'portal' as const,
      on: user.portalAccess,
      // An administrator always has the portal, and nobody closes their own.
      locked: user.role === 'admin' || isSelf,
      title: t('access.doorPortal'),
      hint: t('access.doorPortalHint'),
    },
    {
      key: 'app' as const,
      on: user.appAccess,
      locked: false,
      title: t('access.doorApp'),
      hint: t('access.doorAppHint'),
    },
  ];

  return (
    <div>
      <div className="mb-1.5 text-xs font-semibold text-muted">{t('access.signsInTo')}</div>
      {/* Rows with a switch, not tinted checkbox cards: the tint made "on" look like
          an error state, and a checkbox in a box read as a form field to fill in. */}
      <div className="divide-y divide-border rounded-lg border border-border">
        {doors.map((door) => (
          <div key={door.key} className="flex items-center justify-between gap-4 px-3 py-2.5">
            <div className="min-w-0">
              <div className="text-sm font-semibold">{door.title}</div>
              <div className="mt-0.5 text-xs leading-snug text-muted">{door.hint}</div>
            </div>
            <Switch
              checked={door.on}
              label={door.title}
              disabled={door.locked || disabled || change.isPending}
              onChange={() =>
                change.mutate({
                  portalAccess: door.key === 'portal' ? !door.on : user.portalAccess,
                  appAccess: door.key === 'app' ? !door.on : user.appAccess,
                })
              }
            />
          </div>
        ))}
      </div>
      {change.error ? (
        <p className="mt-2 text-sm text-danger">
          {change.error instanceof ApiError ? change.error.message : t('common.saveError')}
        </p>
      ) : null}
    </div>
  );
}

function PortalGrid({ userId, isSelf, disabled }: { userId: string; isSelf: boolean; disabled: boolean }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const perms = useQuery({ queryKey: ['permissions', userId], queryFn: () => api.userPermissions(userId) });

  const [grid, setGrid] = useState<Grid | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (perms.data) setGrid({ ...perms.data.permissions });
  }, [perms.data]);

  const inherited = useMemo(() => {
    const explicit = perms.data?.explicit ?? {};
    return new Set(SECTIONS.filter((s) => !explicit[s])) as ReadonlySet<Section>;
  }, [perms.data]);

  const dirty = useMemo(() => {
    if (!grid || !perms.data) return false;
    return SECTIONS.some((s) => grid[s] !== perms.data.permissions[s]);
  }, [grid, perms.data]);

  const save = useMutation({
    mutationFn: () => api.setUserPermissions(userId, grid as Grid),
    onSuccess: async () => {
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['permissions', userId] });
      await queryClient.invalidateQueries({ queryKey: ['myPermissions'] });
      setTimeout(() => setSaved(false), 4000);
    },
  });

  if (perms.isLoading || !grid) return <Spinner />;
  if (perms.error) return <ErrorNotice error={perms.error} />;

  const selfLocked = isSelf && grid.users !== 'write';
  const { visible, editable } = summarise(grid);

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-xs font-semibold text-muted">{t('access.portalAccess')}</div>
        <div className="text-xs text-subtle">{t('access.summary', { visible, editable })}</div>
      </div>

      <PermissionGrid value={grid} onChange={setGrid} disabled={disabled} inherited={inherited} />

      {selfLocked ? <p className="mt-3 text-xs text-danger">{t('access.selfLock')}</p> : null}
      {save.error ? (
        <p className="mt-3 text-sm text-danger">
          {save.error instanceof ApiError ? save.error.message : t('common.saveError')}
        </p>
      ) : null}

      {disabled || !(dirty || saved) ? null : (
        // Sticky, so a long grid does not hide the one button that matters. z-20 because
        // the selected option in each row is raised to z-10 to sit over its neighbours'
        // borders, and would otherwise paint straight through this bar.
        <div className="sticky bottom-0 z-20 -mx-1 mt-4 flex items-center gap-3 border-t border-border bg-surface px-1 py-2.5">
          <button
            type="button"
            className="btn-primary"
            disabled={!dirty || save.isPending || selfLocked}
            onClick={() => save.mutate()}
          >
            {save.isPending ? t('common.saving') : t('access.save')}
          </button>
          {dirty ? (
            <button type="button" className="btn-secondary" onClick={() => setGrid({ ...perms.data!.permissions })}>
              {t('access.discard')}
            </button>
          ) : null}
          {saved ? <span className="text-sm font-semibold text-success">{t('access.saved')}</span> : null}
        </div>
      )}
    </div>
  );
}
